/* eslint-disable react/prop-types */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import BookmarkBorder from '@mui/icons-material/BookmarkBorder';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteOutlined from '@mui/icons-material/FavoriteOutlined';
import { withRights } from 'providers/RightsConsumer';
import { RightsContext } from 'providers/RightsContext';
import ProductInfo from 'features/common/components/ProductInfo';
import ProductMessagesComponent from './ProductMessagesComponent';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Dialog, IconButton, Typography } from '@totalsoft/rocket-ui';
import { ProductCard, ProductSelectButton } from './ProductSelectionStyles';
import { BoxActionsWrapper } from 'components/common/CommonStyles';
import { ProductNameText } from './ProductSelectionStyles';
import { useSelector } from 'react-redux';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';
const ProductGridComponent = ({
  idx,
  product,
  t,
  isSelecting,
  isSelected,
  onSelect,
  isSelectable,
  rights,
  computingSimulations,
  areGraceRatesVisible,
  areGuaranteeRatesVisible,
  processing,
  setFavoriteProduct,
  setDeleteProduct,
  ProductIncludedServices,
  graceList,
  errorMessageExists,
  exportSimulation,
  exportingSimulation,
  exportSimulationButtonVisible
}) => {

  const generationStatusInProgress = useSelector(state => state.currentOffer.productSelectionData.flags.simulationsGenerationInProgress);
  const basicDataComputing = (computingSimulations && product.computingStatus === 0) || generationStatusInProgress;
  const fullDataComputing = (computingSimulations && product.computingStatus < 75) || generationStatusInProgress;

  const disabled = !product.isActive || product.needsRecalculation || generationStatusInProgress;

  const [open, setOpen] = useState(false);
  const { financialProductWrite, readOnlyFull } = useContext(RightsContext);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const customIcon = () => {
    return <span>{`${t('OfferEdit.FinancialProducts.Campaign')}`}</span>;
  };

  return (
    <ProductCard
      icon={product.isSpecialAction ? customIcon : BookmarkBorder}
      disabled={disabled}
      selected={isSelected}
    >
      <Grid container alignItems='center' height='8vh'>
        <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
          {product.priority === 1 && (
            <IconButton
              key='DisabledAFavoriteProduct'
              onClick={() => {
                setFavoriteProduct(product, 0);
              }}
              color='primary'
              variant='text'
              size='small'
              disabled={processing || readOnlyFull || !financialProductWrite || product.needsRecalculation}
              sx={{ flexShrink: 0 }}
            >
              <FavoriteOutlined></FavoriteOutlined>
            </IconButton>
          )}
          {(!product.priority || product.priority === 0) && (
            <IconButton
              key='CreateAFavoriteProduct'
              onClick={() => {
                setFavoriteProduct(product, 1);
              }}
              color='primary'
              variant='text'
              size='small'
              disabled={processing || readOnlyFull || !financialProductWrite || product.needsRecalculation}
              sx={{ flexShrink: 0 }}
            >
              <FavoriteBorderIcon></FavoriteBorderIcon>
            </IconButton>
          )}
          <ProductNameText noWrap tooltip={`${product.name}`}>
            {`${product.name}`}
          </ProductNameText>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'end' }}>
          <Typography textTransform='uppercase' variant='subtitle2' fontStyle='italic'>
            {product.contractTypeName}
          </Typography>
        </Grid>
      </Grid>
      <Grid container mt={1}>
        <Grid container spacing={2}>
          <ProductInfo
            name={`${t('OfferEdit.FinancialProducts.Rate')}`}
            secondName={`(${t('OfferEdit.FinancialProducts.WithoutVAT')})`}
            value={`${product.monthlyRateValueWithoutVat === null ? '-' : t('DECIMAL2', { number: product.monthlyRateValueWithoutVat })} ${product.documentCurrencyCode === null ? '-' : product.documentCurrencyCode
              }`}
            computing={fullDataComputing}
          />
          <ProductInfo
            name={`${t('OfferEdit.FinancialProducts.Rate')}`}
            secondName={`(${t('OfferEdit.FinancialProducts.WithVAT')})`}
            value={`${product.monthlyRateValue === null ? '-' : t('DECIMAL2', { number: product.monthlyRateValue })} ${product.documentCurrencyCode === null ? '-' : product.documentCurrencyCode
              }`}
            computing={fullDataComputing}
          />
          <ProductInfo
            name={`${t('OfferEdit.FinancialProducts.Period')}`}
            value={product.periodMonths === null ? '-' : `${product.periodMonths} ${t('OfferEdit.FinancialProducts.Months')}`}
            validationValue={product.periodMonths}
            minValue={product.minPeriod}
            minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minPeriod === null
              ? '-'
              : product.minPeriod === 1
                ? product.minPeriod + ' ' + t('OfferEdit.FinancialProducts.Month')
                : product.minPeriod + ' ' + t('OfferEdit.FinancialProducts.Months')
              }`}
            maxValue={product.maxPeriod}
            maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxPeriod === null
              ? '-'
              : product.maxPeriod === 1
                ? product.maxPeriod + ' ' + t('OfferEdit.FinancialProducts.Month')
                : product.maxPeriod + ' ' + t('OfferEdit.FinancialProducts.Months')
              }`}
            computing={basicDataComputing}
          />
          <ProductInfo
            name={`${t('OfferEdit.FinancialProducts.AdvancePercent')}`}
            value={product.advancePercent === null ? '-' : `${t('PERCENT_NUMBER', { number: product.advancePercent })}%`}
            validationValue={product.advancePercent}
            minValue={product.minAdvancePercent}
            minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minAdvancePercent === null ? '-' : t('PERCENT_NUMBER', { number: product.minAdvancePercent }) + '%'
              }`}
            maxValue={product.maxAdvancePercent}
            maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxAdvancePercent === null ? '-' : t('PERCENT_NUMBER', { number: product.maxAdvancePercent }) + '%'
              }`}
            computing={basicDataComputing}
          />

          {!product.hideVR && (
            <>
              <ProductInfo
                name={
                  product.rezidualFromGrid
                    ? `${t('OfferEdit.FinancialProducts.ResidualValueFromGrid')}`
                    : `${t('OfferEdit.FinancialProducts.ResidualValue')}`
                }
                value={
                  product.rezidualPercent === null || product.hideVR ? '-' : `${t('PERCENT_NUMBER', { number: product.rezidualPercent })}%`
                }
                computing={basicDataComputing}
                validationValue={product.rezidualPercent}
                minValue={product.minRezidualPercent}
                minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minRezidualPercent === null ? '-' : t('PERCENT_NUMBER', { number: product.minRezidualPercent }) + '%'
                  }`}
                maxValue={product.maxRezidualPercent}
                maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxRezidualPercent === null ? '-' : t('PERCENT_NUMBER', { number: product.maxRezidualPercent }) + '%'
                  }`}
              />
            </>
          )}
          <ProductInfo
            name={`${t('OfferEdit.FinancialProducts.ContractAmount')}`}
            secondName={`(${t('OfferEdit.FinancialProducts.WithoutVAT')})`}
            value={`${product.contractValue === null ? '-' : t('DECIMAL2', { number: product.contractValue })} ${product.documentCurrencyCode === null ? '-' : product.documentCurrencyCode
              }`}
            computing={fullDataComputing}
          />
          {areGraceRatesVisible && graceList.length > 0 && (
            <>
              <ProductInfo itemsList={graceList} computing={basicDataComputing} />
            </>
          )}

          {areGuaranteeRatesVisible && (
            <ProductInfo
              name={`${t('OfferEdit.FinancialProducts.NumberOfGuaranteeRates')}`}
              value={product.numberOfGuaranteeRates === null ? '-' : product.numberOfGuaranteeRates}
              computing={basicDataComputing}
              validationValue={product.numberOfGuaranteeRates}
              minValue={product.minNumberOfGuaranteeRates}
              minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minNumberOfGuaranteeRates === null ? '-' : product.minNumberOfGuaranteeRates
                }`}
              maxValue={product.maxNumberOfGuaranteeRates}
              maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxNumberOfGuaranteeRates === null ? '-' : product.maxNumberOfGuaranteeRates
                }`}
            />
          )}
          {product.serviceCategories?.length > 0 && (
            <Grid item xs={12}>
              <Typography textTransform='uppercase' variant='subtitle1'>{`${t('OfferEdit.FinancialProducts.Services')}`}</Typography>
            </Grid>
          )}
          {product.serviceCategories && (
            <Grid item xs={12}>
              <ProductIncludedServices services={product.serviceCategories} />
            </Grid>
          )}
          {product.leasingSimulationMessages && product.leasingSimulationMessages.length > 0 && (
            <Grid item xs={12}>
              <ProductMessagesComponent warnings={product.leasingSimulationMessages} />
            </Grid>
          )}
        </Grid>
        {generationStatusInProgress ? (<Grid item xs={12} mt={1}>
          <WrappedLinearProgress />
        </Grid>) :
          <Grid container alignItems='center' mt={1}>
            <Grid item flexGrow={1} pr={1}>
              <ProductSelectButton
                id={'product_selectBtn_' + idx}
                selected={isSelected}
                onClick={onSelect}
                fullWidth
                disabled={
                  errorMessageExists(product.leasingSimulationMessages) ||
                  !isSelectable ||
                  product.needsRecalculation ||
                  rights.readOnlyFull ||
                  !rights.financialProductWrite ||
                  isSelected ||
                  processing || (!product.isValidated && product.computingStatus < 100)
                }
              >
                {isSelecting ? t('Saving') + '...' : isSelected ? t('Selected') : t('Select')}
              </ProductSelectButton>
              <Dialog
                id={'productGridComponent_dialog'}
                open={open}
                onClose={handleClose}
                content={t('OfferEdit.FinancialProducts.Delete')}
                actions={
                  <BoxActionsWrapper forDialog>
                    <Button onClick={handleClose}>{t('OfferEdit.FinancialProducts.Disagree')}</Button>
                    <Button
                      onClick={() => {
                        setDeleteProduct(product);
                      }}
                      autoFocus
                    >
                      {t('OfferEdit.FinancialProducts.Agree')}
                    </Button>
                  </BoxActionsWrapper>
                }
              ></Dialog>

            </Grid>
            {exportSimulationButtonVisible && (
              <IconButton
                color='primary'
                tooltip={t('Print')}
                disabled={processing || exportingSimulation}
                onClick={exportSimulation}
                variant='text'
              >
                <PrintOutlinedIcon />
              </IconButton>
            )}
            <IconButton
              disabled={isSelected || processing || readOnlyFull || !financialProductWrite}
              color='primary'
              tooltip={t('Delete')}
              variant='text'
              onClick={handleClickOpen}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        }
      </Grid>
    </ProductCard>
  );
};
ProductGridComponent.propTypes = {
  product: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  isSelecting: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  rights: PropTypes.object.isRequired,
  computingSimulations: PropTypes.bool.isRequired,
  idx: PropTypes.number.isRequired,
  areGraceRatesVisible: PropTypes.bool.isRequired,
  areGuaranteeRatesVisible: PropTypes.bool.isRequired,
  processing: PropTypes.bool,
  setFavoriteProduct: PropTypes.func.isRequired,
  setDeleteProduct: PropTypes.func.isRequired,
  graceList: PropTypes.array,
  ProductIncludedServices: PropTypes.func,
  errorMessageExists: PropTypes.func.isRequired,
  exportSimulation: PropTypes.func,
  exportingSimulation: PropTypes.bool,
  exportSimulationButtonVisible: PropTypes.bool
};

export default withTranslation()(withRights(ProductGridComponent));
